import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as yup from 'yup';
import { SubmitHandler } from 'react-hook-form';
import { Form, FormField, SelectInput, SubmitButton } from 'components/Forms';
import { Heading } from 'components/Typography';
import { ContactRelationship, relationshipOptions } from 'domains/client';
import { TherapyTarget } from 'domains/signup/data/types';
import { MiniScreenerContext, withMiniScreenerMachineState } from 'domains/signupv3/Mini-Screener/context';

type EnterRelationshipToClientForm = {
  relationshipToClient: ContactRelationship;
};

const getFormSchema = (t: TFunction): yup.ObjectSchema<EnterRelationshipToClientForm> =>
  yup
    .object({
      relationshipToClient: yup
        .mixed<ContactRelationship>()
        .oneOf(Object.values(ContactRelationship), t('form.validation.fieldIsRequired'))
        .required(t('form.validation.fieldIsRequired')),
    })
    .required();

type Props = {
  defaultValues?: {
    relationshipToClient?: ContactRelationship;
  };
  onSubmit: SubmitHandler<EnterRelationshipToClientForm>;
  target: TherapyTarget;
};

const EnterRelationshipToClient: React.FC<Props> = ({ defaultValues, onSubmit }) => {
  const { t } = useTranslation();

  return (
    <>
      <Form
        onSubmit={onSubmit}
        schema={getFormSchema(t)}
        defaultValues={defaultValues}
        validationMode="all"
        flashErrors={false}
      >
        <Heading level="h2" className="mb-64">
          {t(`miniScreener.steps.enterRelationshipToClient.title`)}
        </Heading>

        <FormField
          allowEmpty
          component={SelectInput}
          name="relationshipToClient"
          data-testid="relationship-to-client-select"
          label={t(`miniScreener.steps.enterRelationshipToClient.label`)}
          options={relationshipOptions}
          placeholder={t(`miniScreener.steps.enterRelationshipToClient.placeholder`)}
        />

        <SubmitButton
          data-testid="enter-relationship-to-client-submit-button"
          className="mt-64 sm-d:mt-40 sm-d:w-full"
          size="lg"
          disableIfInvalid
          willRedirect={false}
        >
          {t('miniScreener.steps.enterRelationshipToClient.submit')}
        </SubmitButton>
      </Form>
    </>
  );
};

const MiniScreenerWrapper = () => {
  const target = MiniScreenerContext.useSelector(({ context }) => context.input.target);
  const relationshipToClient = MiniScreenerContext.useSelector(({ context }) => context.data.relationshipToClient);
  const { send } = MiniScreenerContext.useActorRef();

  const onSubmit: SubmitHandler<EnterRelationshipToClientForm> = useCallback(
    ({ relationshipToClient }) => {
      send({
        type: 'submitRelationshipToClient',
        relationshipToClient,
      });
    },
    [send],
  );

  return (
    <EnterRelationshipToClient
      defaultValues={{
        relationshipToClient,
      }}
      onSubmit={onSubmit}
      target={target as TherapyTarget}
    />
  );
};

export const MiniScreenerEnterRelationshipToClient = withMiniScreenerMachineState(
  MiniScreenerWrapper,
  'enterRelationshipToClient',
);
